import { type ReactElement, type ReactNode } from 'react';
import { Link } from './Link';
import './PageTabs.css';

export interface PageTabItem {
  /** Stable identity of the tab; also its React key. */
  key: string;
  label: ReactNode;
  /** Route tab: the sub-route this tab lands on. Omit for a filter tab. */
  to?: string;
  /** Filter tab: swaps the list under the same URL. Omit for a route tab. */
  onSelect?: () => void;
  /** `data-log` name. Analytics owns these strings — never rename in passing. */
  log?: string;
  /** Optional trailing mark, e.g. the unread badge. */
  badge?: ReactNode;
}

/**
 * The one horizontal tab line (UI standard §6): a hairline rule under the row
 * with the active tab carrying an accent underline. Two kinds of tab live in
 * the same skin — route tabs that navigate (Profile) and filter tabs that swap
 * the list under one URL (/requests) — and they differ only in semantics, so
 * the markup differs and the look does not. A page that needs tabs mounts this
 * instead of restyling a local row.
 */
export function PageTabs({
  label,
  active,
  items,
  className = '',
}: {
  /** Accessible name for the group. */
  label: string;
  /** `key` of the tab currently showing. */
  active: string;
  items: PageTabItem[];
  className?: string;
}): ReactElement {
  const routed = items.some((item) => item.to !== undefined);
  const rowClass = `ember-tabs${className ? ` ${className}` : ''}`;
  const tabClass = (item: PageTabItem): string => `ember-tab${item.key === active ? ' is-active' : ''}`;
  const body = (item: PageTabItem): ReactElement => (
    <>
      <span className="ember-tab-label">{item.label}</span>
      {item.badge}
    </>
  );

  // Route tabs are links in a nav: they are page navigation, and the browser's
  // own affordances (open in a new tab, Back) have to keep working.
  if (routed) {
    return (
      <nav className={rowClass} aria-label={label}>
        {items.map((item) => (
          <Link
            key={item.key}
            to={item.to as string}
            replace
            className={tabClass(item)}
            aria-current={item.key === active ? 'page' : undefined}
            data-log={item.log}
          >
            {body(item)}
          </Link>
        ))}
      </nav>
    );
  }

  return (
    <div className={rowClass} role="tablist" aria-label={label}>
      {items.map((item) => (
        <button
          key={item.key}
          type="button"
          role="tab"
          aria-selected={item.key === active}
          className={tabClass(item)}
          data-log={item.log}
          onClick={item.onSelect}
        >
          {body(item)}
        </button>
      ))}
    </div>
  );
}
